export const LANGUAGE_CODES = [
  "es_cl",
  "es_ar",
  "en_us",
  "es_pe",
  "pt_br",
  "es_co",
  "es_uy",
  "es_py",
  "es_ec",
  "es_es",
  "en_gb",
  "es_do",
] as const;

const localeToCurrency: Record<string, string> = {
  en_us: "USD", // US Dollar
  es_cl: "CLP", // Chilean Peso
  es_ar: "ARS", // Argentine Peso
  es_pe: "PEN", // Peruvian Sol
  pt_br: "BRL", // Brazilian Real
  es_co: "COP", // Colombian Peso
  es_uy: "USD", // Uruguayan Peso
  es_py: "USD", // Paraguayan Guarani
  es_ec: "USD", // Ecuador uses US Dollar
  es_do: "USD", // Dominican Republic uses US Dollar
};

const currencyToLocale: Record<string, string> = {
  CLP: "es-CL",
  ARS: "es-AR",
  COP: "es-CO",
  PEN: "es-PE",
  BRL: "pt-BR",
  USD: "en-US",
};

export const localeCurrencyMap: Record<string, string[]> = {
  // 🇨🇱 Chile
  "es-CL": ["USD", "CLP", "BRL", "COP", "PEN"],

  // 🇨🇴 Colombia
  "es-CO": ["USD", "COP"],

  // 🇵🇪 Peru
  "es-PE": ["USD", "CLP", "BRL", "COP", "PEN"],

  // 🇦🇷 Argentina
  "es-AR": ["ARS"],

  // 🇺🇸 United States
  "en-US": ["USD", "CLP", "BRL", "COP", "PEN"],

  // 🇵🇾 Paraguay
  "es-PY": ["USD", "CLP", "BRL", "COP", "PEN"],

  // 🇺🇾 Uruguay
  "es-UY": ["USD", "CLP", "BRL", "COP", "PEN"],

  // 🇧🇷 Brazil
  "pt-BR": ["USD", "CLP", "BRL", "COP", "PEN"],

  // 🇪🇨 Ecuador
  "es-EC": ["USD", "CLP", "BRL", "COP", "PEN"],

  // 🇩🇴 Dominican Republic
  "es-DO": ["USD", "CLP", "BRL", "COP", "PEN"],

  //  Generic fallback (for unknown or global Spanish)
  es: ["USD", "CLP", "BRL", "COP", "PEN"],

  //  Generic fallback (for unknown English locales)
  en: ["USD", "CLP", "BRL", "COP", "PEN"],
};

export const CURRENCY_COOKIE = "currency";
export const DEFAULT_CURRENCY = "CLP";
export type LanguageCode = (typeof LANGUAGE_CODES)[number];

export const LOCALE_COOKIE = "locale";
// Request/response header the middleware injects with the URL-derived locale code.
// Defined here (dependency-free) so both server.ts and middleware.ts can read it
// without a circular import; middleware.ts re-exports it for its public surface.
export const LOCALE_HEADER = "x-locale";
export const COUNTRY_COOKIE = "selected_country";
export const DEFAULT_COUNTRY_CODE = "CL";
export const DEFAULT_LANGUAGE_CODE: LanguageCode = "es_cl";

export function toCanonical(input: string): string {
  const cleaned = input.replace("_", "-").trim();
  const [lang, region] = cleaned.split("-");
  if (!lang) return "";
  if (!region) return lang.toLowerCase();
  return `${lang.toLowerCase()}-${region.toUpperCase()}`;
}

export function toSnake(input: string): string {
  const [lang, region] = input.replace("-", "_").split("_");
  if (!lang) return "";
  return region ? `${lang.toLowerCase()}_${region.toLowerCase()}` : lang.toLowerCase();
}

export function isLanguageCode(v: string): v is LanguageCode {
  return (LANGUAGE_CODES as readonly string[]).includes(v);
}

export function normalizeCode(input?: string | null): LanguageCode | null {
  if (!input) return null;
  const snake = toSnake(input);
  return isLanguageCode(snake) ? (snake as LanguageCode) : null;
}

export function toCMSLocale(code: LanguageCode | string): string {
  return toCanonical(code);
}

export function getCurrencyFromLocale(locale: string): string {
  return localeToCurrency[toSnake(locale)] || DEFAULT_CURRENCY; // default fallback
}

// Picks a valid currency to fall back to when a previously selected one is no longer among the available options.
export function resolveFallbackCurrency(locale: string, availableCurrencies: string[]): string {
  const localeDefault = getCurrencyFromLocale(locale);
  if (availableCurrencies.includes(localeDefault)) return localeDefault;
  return availableCurrencies[0] ?? DEFAULT_CURRENCY;
}

export function getLocaleFromCurrency(currency: string): string {
  return currencyToLocale[currency] ?? "en-US";
}

export function getCountryCode(culture: string): string {
  const parts = culture?.replace("_", "-").split("-");
  return parts?.[1]?.toUpperCase() ?? DEFAULT_COUNTRY_CODE;
}

export enum LOCALE_CODES {
  Chile = "es-CL",
  Argentina = "es-AR",
  Peru = "es-PE",
  Ecuador = "es-EC",
  Colombia = "es-CO",
  Brazil = "pt-BR",
  Uruguay = "es-UY",
  Paraguay = "es-PY",
  USA = "en-US",
  DominicanRepublic = "es-DO",
}
export const COUNTRIES = [
  {
    countryCode: "CL",
    localeCode: "es_cl",
    name: "Chile",
  },
  {
    countryCode: "US",
    localeCode: "en_us",
    name: "English",
  },
  {
    countryCode: "EC",
    localeCode: "es_ec",
    name: "Ecuador",
  },
  {
    countryCode: "PE",
    localeCode: "es_pe",
    name: "Perú",
  },
  {
    countryCode: "AR",
    localeCode: "es_ar",
    name: "Argentina",
  },
  {
    countryCode: "BR",
    localeCode: "pt_br",
    name: "Brasil",
  },
  {
    countryCode: "CO",
    localeCode: "es_co",
    name: "Colombia",
  },
  {
    countryCode: "PY",
    localeCode: "es_py",
    name: "Paraguay",
  },
  {
    countryCode: "UY",
    localeCode: "es_uy",
    name: "Uruguay",
  },
  {
    countryCode: "DO",
    localeCode: "es_do",
    name: "Rep. Dom",
  },
];

// ────────────────────────────────────────────────────────────────────────────
// URL path/slug helpers — locale lives at the ROOT as two slugs: /{country}/{lang}
// Both slugs are lowercase; order is country then language (e.g. "/cl/es").
// Internal code stays the existing LanguageCode ("es_cl"), built as `${lang}_${country}`.
// ────────────────────────────────────────────────────────────────────────────

// path slugs: country then language, both lowercase — e.g. "es_cl" -> { country: "cl", lang: "es" }
export function languageCodeToPath(code: LanguageCode): { country: string; lang: string } {
  const [lang, country] = code.split("_"); // "es_cl" -> ["es","cl"]
  return { country: country!.toLowerCase(), lang: lang!.toLowerCase() };
}

export function pathToLanguageCode(country?: string, lang?: string): LanguageCode | null {
  if (!country || !lang) return null;
  const candidate = `${lang.toLowerCase()}_${country.toLowerCase()}`;
  return isLanguageCode(candidate) ? (candidate as LanguageCode) : null;
}

export function isValidLocalePath(country?: string, lang?: string): boolean {
  return pathToLanguageCode(country, lang) !== null;
}

// build the root prefix "/cl/es" (no trailing slash; caller appends the rest)
export function buildLocalePath(code: LanguageCode): string {
  const { country, lang } = languageCodeToPath(code);
  return `/${country}/${lang}`;
}

// parse the first two slugs (locale lives at the ROOT); return code + remaining path
export function parseLocalePath(pathname: string): { code: LanguageCode | null; rest: string } {
  const [, country, lang, ...rest] = pathname.split("/"); // ["", "cl", "es", "booking", ...]
  const code = pathToLanguageCode(country, lang);
  return { code, rest: code ? `/${rest.join("/")}` : pathname };
}

// convenience wrappers used by the navigation layer (the URL path is the source of truth)
export function getActiveLocaleFromPath(pathname: string): LanguageCode | null {
  return parseLocalePath(pathname).code;
}

export function stripLocaleFromPath(pathname: string): string {
  return parseLocalePath(pathname).rest;
}

// prefix an internal absolute path with a locale, e.g.
// withLocalePrefix("/booking/flight", "es_cl") -> "/cl/es/booking/flight". Leaves
// external / protocol-relative / relative paths untouched. Idempotent: a path that
// already carries a valid /{country}/{lang} prefix is returned unchanged (prevents
// "/cl/es/cl/es/..." double-prefixing when an already-localized href is passed in).
export function withLocalePrefix(path: string, code: LanguageCode): string {
  if (!(path.startsWith("/") && !path.startsWith("//"))) return path;
  if (getActiveLocaleFromPath(path)) return path;
  return `${buildLocalePath(code)}${path}`;
}

// ── country → default locale/lang mapping (from COUNTRIES) ──
// If a country ever supports multiple languages, extend this map explicitly here.
export function getDefaultLocaleForCountry(countryCode?: string): LanguageCode | null {
  if (!countryCode) return null;
  const match = COUNTRIES.find((c) => c.countryCode === countryCode.toUpperCase());
  return match ? (match.localeCode as LanguageCode) : null;
}

export function getDefaultLangForCountry(countryCode: string): string | null {
  const code = getDefaultLocaleForCountry(countryCode);
  return code ? languageCodeToPath(code).lang : null;
}

// ── non-localized path prefixes (assets + API routes) ──
// Single source of truth reused by localeMiddleware AND fetchInterceptor so "what is
// not localized" is defined once. API routes (incl. `api-site`) never get a locale prefix.
export const NON_LOCALIZED_PREFIXES = ["/_next", "/api", "/api-site"] as const;

export function isNonLocalizedPath(p: string): boolean {
  return (
    NON_LOCALIZED_PREFIXES.some((pre) => p === pre || p.startsWith(`${pre}/`)) || /\.[^/]+$/.test(p) // files with an extension (favicon, images, etc.)
  );
}
